import { DELIVERYTYPE_STOREPICKUP } from '@krispsystems/constants';
import { formValueSelector } from 'redux-form';
import { createSelector } from 'reselect';
import { getParticularPreferenceSettings } from '../../../common/selectors/preference';

export const getControlRecords = createSelector([(state) => state.oms.list.data], (list) => {
  return list;
});

export const isFetchingControlRecords = createSelector([(state) => state.oms.list.is_fetching], (isFetching) => {
  return isFetching;
});

export const getStickyState = createSelector([(state) => state.oms.sticky, (state, key) => key], (state, key) => {
  return state[key];
});

export const getPagination = createSelector(
  [
    (state) => state.oms.list,
    (state) => {
      const locationId = getOmsLocationId(state);
      const subsidiaryId = getOmsSubsidiaryId(state);
      const { value } = getParticularPreferenceSettings(
        state,
        'search_default_rows_count_list',
        ['value'],
        locationId,
        subsidiaryId,
      );
      return Number(value);
    },
    (state, useDefault) => useDefault,
  ],
  (data, defaultRowsPerPage, useDefault) => {
    return {
      requested_page: useDefault ? 1 : data.requested_page,
      rows_per_page: useDefault ? defaultRowsPerPage : data.rows_per_page || defaultRowsPerPage,
      total_rows: data.total_rows,
      page: data.page,
      is_fetching: data.is_fetching,
      sorted_columns: data.sorted_columns,
    };
  },
);

export const getFilter = createSelector([(state) => state.oms.list], (data) => {
  return {
    requested_page: data.requested_page,
    rows_per_page: data.rows_per_page,
    total_rows: data.total_rows,
    page: data.page,
    is_fetching: data.is_fetching,
    sorted_columns: data.sorted_columns,
    created_from: data.created_from,
    created_to: data.created_to,
    shipping_method_id: data.shipping_method_id,
    subsidiary_id: data.subsidiary_id,
    location_id: data.location_id,
    delivery_type_id: data.delivery_type_id,
    abandon: false,
    ship: false,
    pick: data.pick,
    pack: data.pack,
  };
});

export const selectedControlRecords = createSelector([(state) => state.oms.selected.initial.data], (data) => {
  return data;
});

export const pickedControlRecords = createSelector([(state) => state.oms.selected.pick.data], (data) => {
  return data;
});

export const packedShippedControlRecords = createSelector([(state) => state.oms.selected.pack_ship.data], (data) => {
  return data;
});

export const getPackShipForm = createSelector([(state) => state.oms.pack_ship.form], (data) => {
  return data || {};
});

export const isPickupStore = createSelector(
  [
    (state) => {
      const selector = formValueSelector('OrderManagementMain');
      return selector(state, 'delivery_type');
    },
    (state) => {
      const form = getPickForm(state);
      return form.delivery_type;
    },
    (state) => {
      const form = getPackShipForm(state);
      return form.delivery_type;
    },
  ],
  (deliveryType1, deliveryType2, deliveryType3) => {
    const data = deliveryType1 || deliveryType2 || deliveryType3 || {};
    return data.cid === DELIVERYTYPE_STOREPICKUP;
  },
);

export const getPickForm = createSelector([(state) => state.oms.pick.form], (data) => {
  return data || {};
});

export const getPickList = createSelector([(state) => state.oms.pick.list], (list) => list || []);

export const getPackList = createSelector([(state) => state.oms.pack_ship.list], (list) => list || []);

export const formControlRecord = createSelector([(state) => state.oms.selected.form], (data) => {
  return data;
});

export const isListLoading = createSelector([(state) => state.oms.selected.is_loading], (isLoading) => {
  return isLoading;
});

export const getBook = createSelector(
  [(state) => state.oms.pack_ship.book, (_, id) => id],
  (book, id) => book[id] || {},
);

export const isListBooking = createSelector([(state) => state.oms.pack_ship.book], (book) => {
  if (!book) return false;
  const books = Object.values(book);
  const isBooking = books.some(({ isBooking }) => isBooking);
  return isBooking;
});

export const isListCancelBooking = createSelector([(state) => state.oms.pack_ship.book], (book) => {
  if (!book) return false;
  const books = Object.values(book);
  const isCancelBooking = books.some(({ isCancelBooking }) => isCancelBooking);
  return isCancelBooking;
});

export const getSelectedPackShip = createSelector(
  [(state) => state.oms.selected.pack_ship.data, (_, id) => id],
  (data, id) => data.get(id),
);

export const getOmsLocation = createSelector([(state) => state.oms.list], (list) => list.location);
export const getOmsLocationId = createSelector([(state) => state.oms.list], (list) => list.location_id);
export const getOmsSubsidiaryId = createSelector([(state) => state.oms.list], (list) => list.subsidiary_id);

export const getOmsBarcodeScanModalIsActive = createSelector([(state) => state.oms.modal], (modal) => {
  return modal.barcode_scanner.isModalActive;
});

export const getOmsBarcodeScanModalIsLoading = createSelector([(state) => state.oms.modal], (modal) => {
  return modal.barcode_scanner.isModalLoading;
});

export const getOmsBarcodeScanData = createSelector([(state) => state.oms.modal.barcode_scanner], ({ data }) => data);
export const getOmsBarcodeScanError = createSelector(
  [(state) => state.oms.modal.barcode_scanner],
  ({ error }) => error,
);

export const isPickLoading = createSelector(
  [(state) => state.oms.loading, (state, orderId) => orderId],
  ({ picked }, orderId) => picked.indexOf(orderId) > -1,
);

export const isPackLoading = createSelector(
  [(state) => state.oms.loading, (state, orderId) => orderId],
  ({ packed }, orderId) => packed.indexOf(orderId) > -1,
);

export const isShipLoading = createSelector(
  [(state) => state.oms.loading, (state, orderId) => orderId],
  ({ shipped }, orderId) => shipped.indexOf(orderId) > -1,
);

export const isRejectLoading = createSelector(
  [(state) => state.oms.loading, (state, orderId) => orderId],
  ({ reject }, orderId) => reject.indexOf(orderId) > -1,
);

export const isAssignWaveLoading = createSelector([(state) => state.oms.loading], ({ assign_wave }) => assign_wave);

export const isHoldLoading = createSelector([(state) => state.oms.loading], ({ hold }) => hold);

export const isUnholdLoading = createSelector([(state) => state.oms.loading], ({ unhold }) => unhold);

export const isShipOrdersLoading = createSelector(
  [(state) => state.oms.loading],
  ({ shipping_orders }) => shipping_orders,
);

export const getOmsPrinting = createSelector([(state) => state.oms.printing], (printing) => printing);

export const getOmsPrintingByType = createSelector(
  [(state) => state.oms.printing, (_, deliveryType) => deliveryType, (_, __, type) => type],
  (printing, deliveryType, type) => printing[deliveryType][type],
);

export const getOmsPrintingInProgressByType = createSelector(
  [(state) => state.oms.printing, (_, deliveryType) => deliveryType, (_, __, type) => type],
  (printing, deliveryType, type) =>
    printing && printing[deliveryType] && printing[deliveryType][type] && printing[deliveryType][type].isLoading,
);

export const getOmsPrintingErrorByType = createSelector(
  [(state) => state.oms.printing, (_, deliveryType) => deliveryType, (_, __, type) => type],
  (printing, deliveryType, type) => ({
    isError: Boolean(printing[deliveryType][type].isError),
    message: printing[deliveryType][type].error,
  }),
);
